import { STICKER_HEADERS, type ImportedStickerRow } from './domain'
import { normalizeText } from './normalization'

type CsvRecord = Record<string, string>

function detectDelimiter(firstLine: string): string {
  const candidates = [',', ';', '\t']
  return candidates.sort((a, b) => firstLine.split(b).length - firstLine.split(a).length)[0]
}

function parseCsvRows(input: string, delimiter: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let value = ''
  let quoted = false
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index]
    if (char === '"') {
      if (quoted && input[index + 1] === '"') {
        value += '"'
        index += 1
      } else quoted = !quoted
    } else if (char === delimiter && !quoted) {
      row.push(value)
      value = ''
    } else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && input[index + 1] === '\n') index += 1
      row.push(value)
      if (row.some((cell) => cell.trim())) rows.push(row)
      row = []
      value = ''
    } else value += char
  }
  row.push(value)
  if (row.some((cell) => cell.trim())) rows.push(row)
  return rows
}

function get(record: CsvRecord, label: string): string {
  const normalizedLabel = normalizeText(label)
  const key = Object.keys(record).find((candidate) => normalizeText(candidate) === normalizedLabel)
  return key ? record[key].trim() : ''
}

function parseQuantity(value: string): number {
  const quantity = Number.parseInt(value.replace(/\D/g, ''), 10)
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
}

function parseModels(value: string): string[] {
  return value
    .split(/[|,;]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function parseConsent(value: string): boolean {
  return ['SIM', 'TRUE', '1', 'ACEITO', 'ACEITA'].includes(normalizeText(value))
}

export function parseStickerCsv(input: string): ImportedStickerRow[] {
  const clean = input.replace(/^\uFEFF/, '')
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? ''
  const delimiter = detectDelimiter(firstLine)
  const matrix = parseCsvRows(clean, delimiter)
  if (matrix.length < 2) throw new Error('O arquivo não contém pedidos')
  const headers = matrix[0].map((header) => header.trim())
  const required = ['Nome', 'WhatsApp', 'CEP', 'Logradouro', 'Número', 'Bairro', 'Cidade', 'Estado']
  const missing = required.filter((label) => !headers.some((header) => normalizeText(header) === normalizeText(label)))
  if (missing.length) throw new Error(`Colunas obrigatórias ausentes: ${missing.join(', ')}`)

  return matrix.slice(1).map((values, index) => {
    const record = Object.fromEntries(headers.map((header, column) => [header, values[column] ?? '']))
    return {
      sourceRowNumber: index + 2,
      receivedAt: get(record, STICKER_HEADERS[0]),
      source: get(record, STICKER_HEADERS[2]),
      page: get(record, STICKER_HEADERS[3]),
      recipientName: get(record, STICKER_HEADERS[4]),
      phone: get(record, STICKER_HEADERS[5]),
      cep: get(record, STICKER_HEADERS[6]),
      street: get(record, STICKER_HEADERS[7]),
      streetNumber: get(record, STICKER_HEADERS[8]),
      complement: get(record, STICKER_HEADERS[9]),
      neighborhood: get(record, STICKER_HEADERS[10]),
      city: get(record, STICKER_HEADERS[11]),
      state: get(record, STICKER_HEADERS[12]),
      referencePoint: get(record, STICKER_HEADERS[13]),
      quantity: parseQuantity(get(record, STICKER_HEADERS[14])),
      consent: parseConsent(get(record, STICKER_HEADERS[15])),
      status: get(record, STICKER_HEADERS[16]),
      utmSource: get(record, STICKER_HEADERS[17]),
      utmMedium: get(record, STICKER_HEADERS[18]),
      utmCampaign: get(record, STICKER_HEADERS[19]),
      externalId: get(record, STICKER_HEADERS[21]),
      stickerModels: parseModels(get(record, STICKER_HEADERS[22])),
    }
  })
}

export function supportedHeaders(): string[] {
  return [...STICKER_HEADERS]
}
